import { useState, useEffect } from 'react';
import { alphalendWebSocketService } from '../services/alphalendWebsocket';
export const useAlphalendEventSubscription = (room, eventName) => {
    // Use the unified caching system from the service
    const [data, setData] = useState(() => {
        if (!room || !eventName)
            return null;
        return alphalendWebSocketService.getCachedEventData(room, eventName);
    });
    const [isLoading, setIsLoading] = useState(!!room && !!eventName && !data);
    const [error, setError] = useState(null);
    useEffect(() => {
        // Don't subscribe if room or eventName is null/empty
        if (!room || !eventName) {
            setData(null);
            setIsLoading(false);
            setError(null);
            return;
        }
        // Clear any previous errors
        setError(null);
        // Check for cached data first
        const cachedData = alphalendWebSocketService.getCachedEventData(room, eventName);
        if (cachedData) {
            setData(cachedData);
            setIsLoading(false);
        }
        const callback = (message) => {
            try {
                if (message.event === eventName) {
                    const eventData = message.data;
                    setData(eventData);
                    setIsLoading(false);
                    setError(null); // Clear any previous errors
                }
            }
            catch (err) {
                console.error(`Error processing event ${eventName} for room ${room}:`, err);
                setError(`Failed to process ${eventName} event`);
            }
        };
        alphalendWebSocketService.subscribeMessage(room, callback);
        return () => {
            alphalendWebSocketService.unsubscribeMessage(room, callback);
        };
    }, [room, eventName]);
    useEffect(() => {
        const unsubscribe = alphalendWebSocketService.onConnectionStatusChange(() => {
            const connectionStatus = alphalendWebSocketService.getConnectionStatus();
            // Handle different connection states
            switch (connectionStatus) {
                case 'connecting':
                case 'reconnecting':
                    // Only show loading if we don't have data to display
                    if (!data) {
                        setIsLoading(true);
                    }
                    break;
                case 'connected':
                    // Don't immediately hide loading - let fresh data arrival hide it
                    break;
                case 'disconnected':
                    // Keep showing cached data without loading spinner
                    setIsLoading(false);
                    break;
                case 'failed':
                    // Show error state but keep cached data
                    setError('Connection failed');
                    setIsLoading(false);
                    break;
            }
        });
        return unsubscribe;
    }, [data]);
    // Re-read from cache when tab becomes visible to avoid empty dropdown during reconnect
    useEffect(() => {
        if (!room || !eventName)
            return;
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                const cachedData = alphalendWebSocketService.getCachedEventData(room, eventName);
                if (cachedData) {
                    setData(cachedData);
                    setIsLoading(false);
                }
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [room, eventName]);
    return { data, isLoading, error };
};
